/**
 * Límites que escribe el usuario (tiempo libre y presupuesto) y cómo se aplican a los sitios.
 * Lógica pura (sin red ni React) compartida por Android, web y Windows (que carga la web).
 */
import { dwellFallbackMinutes } from "./dwellAi";
import { SPEED_KMH, type SettingPref, type TravelMode } from "./plans";
import { PAID_KINDS } from "./placeCatalog";
import type { NearbyPlace, SocialContext } from "./types";

export const MIN_FREE_MINUTES = 15;
export const MAX_FREE_MINUTES = 24 * 60;
export const MAX_BUDGET_EUR = 5000;

/** Atajos rápidos de tiempo (minutos). La entrada manual siempre manda. */
export const TIME_SHORTCUTS = [30, 60, 90, 120, 180, 240];
/** Atajos rápidos de presupuesto (€). null = sin límite. */
export const BUDGET_SHORTCUTS: Array<number | null> = [0, 10, 20, 50, null];

/** ok=false ⇒ error trae el mensaje para el usuario. */
export type ParsedFreeTime = { ok: boolean; minutes: number; error: string };
export type ParsedBudget = { ok: boolean; budget: number | null; error: string };

function timeError(error: string): ParsedFreeTime {
  return { ok: false, minutes: 0, error };
}

function budgetError(error: string): ParsedBudget {
  return { ok: false, budget: null, error };
}

function normalizeNumberText(raw: string): string {
  return String(raw ?? "")
    .trim()
    .replace(/€/g, "")
    .replace(/\s+/g, "")
    .replace(",", ".");
}

/**
 * Tiempo libre escrito a mano: horas (admite «1,5») + minutos.
 * Vacío = 0. Debe quedar entre 15 minutos y 24 horas.
 */
export function parseFreeTime(hoursText: string, minutesText: string): ParsedFreeTime {
  const h = normalizeNumberText(hoursText);
  const m = normalizeNumberText(minutesText);
  if (h && !/^\d{1,2}(\.\d{1,2})?$/.test(h)) return timeError("Las horas deben ser un número (por ejemplo 2 o 1,5).");
  if (m && !/^\d{1,4}$/.test(m)) return timeError("Los minutos deben ser un número entero (por ejemplo 30).");
  const total = Math.round((h ? Number(h) : 0) * 60 + (m ? Number(m) : 0));
  if (!Number.isFinite(total) || total <= 0) return timeError("Escribe cuánto tiempo libre tienes.");
  if (total < MIN_FREE_MINUTES) return timeError(`El tiempo libre mínimo es de ${MIN_FREE_MINUTES} minutos.`);
  if (total > MAX_FREE_MINUTES) return timeError("El tiempo libre máximo es de 24 horas.");
  return { ok: true, minutes: total, error: "" };
}

/** Reparte minutos en los dos campos (para los atajos). */
export function splitMinutes(total: number): { hours: string; minutes: string } {
  const safe = Math.max(0, Math.round(total));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return { hours: hours ? String(hours) : "", minutes: minutes ? String(minutes) : hours ? "" : "0" };
}

/** Texto legible: «1 h 30 min», «45 min». */
export function formatMinutes(total: number): string {
  const safe = Math.max(0, Math.round(total));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Presupuesto por persona escrito a mano en €.
 * Vacío = sin límite. «0» o «gratis» = solo planes gratis.
 */
export function parseBudget(text: string): ParsedBudget {
  const raw = String(text ?? "").trim().toLowerCase();
  if (!raw || /^sin\s*l[ií]mite$/.test(raw)) return { ok: true, budget: null, error: "" };
  if (/^gratis$/.test(raw)) return { ok: true, budget: 0, error: "" };
  const n = normalizeNumberText(raw);
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(n)) return budgetError("El presupuesto debe ser una cantidad en euros (por ejemplo 15 o 12,50).");
  const value = Number(n);
  if (!Number.isFinite(value) || value < 0) return budgetError("El presupuesto no puede ser negativo.");
  if (value > MAX_BUDGET_EUR) return budgetError(`El presupuesto máximo es de ${MAX_BUDGET_EUR} €.`);
  return { ok: true, budget: Math.round(value * 100) / 100, error: "" };
}

export function formatBudget(budget: number | null): string {
  if (budget == null) return "sin límite";
  if (budget === 0) return "gratis";
  return `${String(budget).replace(".", ",")} €`;
}

/** Coste orientativo por persona (€) de cada tipo de sitio en España. */
const KIND_COST_EUR: Record<string, number> = {
  cafe: 3,
  bakery: 3,
  bar: 8,
  restaurant: 20,
  fast_food: 9,
  mcdonalds: 9,
  burgerking: 9,
  cinema: 9,
  theatre: 20,
  supermarket: 5,
  shop: 15,
  clothes: 25,
  furniture: 20,
  hardware: 10,
  doityourself: 10,
  mall: 15,
  books: 12,
  bowling: 8,
  nightclub: 15,
  kart: 20,
  ski: 50,
  bungee: 60,
  parachuting: 200,
  museum: 8,
  zoo: 20,
  aquarium: 18,
  theme_park: 45,
  water_park: 30,
  circus: 15,
  escape_game: 20,
  casino: 20,
  spa: 30,
  fitness: 10,
  hotel: 80,
  hostel: 25,
  golf: 40,
  paintball: 25,
  swimming: 5,
};

export function estimatedCostEur(place: Pick<NearbyPlace, "kind" | "paid" | "isEvent">): number {
  if (place.paid === false) return 0;
  const known = KIND_COST_EUR[place.kind];
  if (known != null) return known;
  if (place.paid === true || PAID_KINDS.has(place.kind)) return 10;
  return 0;
}

export function fitsBudget(place: Pick<NearbyPlace, "kind" | "paid" | "isEvent">, budget: number | null | undefined): boolean {
  if (budget == null) return true;
  return estimatedCostEur(place) <= budget;
}

/** Minutos de ida y vuelta al sitio según cómo vas. */
export function roundTripMinutes(distanceMeters: number | undefined, mode: TravelMode): number {
  const km = Math.max(0, Number(distanceMeters) || 0) / 1000;
  const v = SPEED_KMH[mode] ?? 4;
  return Math.round(((km * 2) / v) * 60);
}

/** Tiempo mínimo razonable de visita (60 % de lo habitual, nunca menos de 10 min). */
export function minVisitMinutes(kind: string): number {
  return Math.max(10, Math.round(dwellFallbackMinutes(kind) * 0.6));
}

/** ¿Da tiempo a ir, estar un rato y volver? */
export function fitsTime(place: Pick<NearbyPlace, "kind" | "distanceMeters">, freeMinutes: number, mode: TravelMode): boolean {
  return roundTripMinutes(place.distanceMeters, mode) + minVisitMinutes(place.kind) <= freeMinutes;
}

/** Minutos de estancia que caben en el tiempo libre. */
export function visitMinutesFor(place: Pick<NearbyPlace, "kind" | "distanceMeters">, freeMinutes: number, mode: TravelMode): number {
  const left = freeMinutes - roundTripMinutes(place.distanceMeters, mode);
  return Math.max(10, Math.min(dwellFallbackMinutes(place.kind), left));
}

export type PlanFilterSnapshot = {
  minutes: number | null;
  budget: number | null | "invalid";
  mode: TravelMode;
  setting: SettingPref;
  social: SocialContext;
  mood?: number;
  locationKey?: string;
  /** Categorías de actividad marcadas (vacío = todas). */
  categories?: string[];
};

/** Clave estable para saber si los filtros han cambiado desde la última búsqueda. */
export function planFiltersKey(snapshot: PlanFilterSnapshot): string {
  return JSON.stringify([
    snapshot.minutes,
    snapshot.budget,
    snapshot.mode,
    snapshot.setting,
    snapshot.social,
    snapshot.mood == null ? null : Math.round(snapshot.mood * 20) / 20,
    snapshot.locationKey ?? "",
    [...(snapshot.categories ?? [])].sort(),
  ]);
}
